import React, { useState, useEffect, useContext } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Box,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  CardMedia,
  Grid,
  IconButton,
  Tooltip
} from "@material-ui/core";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Image as ImageIcon,
  VideoLibrary as VideoIcon
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  searchContainer: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  statusCard: {
    marginBottom: theme.spacing(2),
    border: "1px solid #e0e0e0",
  },
  statusText: {
    marginBottom: theme.spacing(1),
    wordBreak: "break-word",
  },
  mediaContainer: {
    position: "relative",
    marginBottom: theme.spacing(1),
  },
  media: {
    height: 200,
    objectFit: "cover",
    borderRadius: theme.spacing(1),
  },
  statusChip: {
    fontSize: "0.75rem",
    height: 24,
  },
  activeStatus: {
    backgroundColor: "#4caf50",
    color: "white",
  },
  expiredStatus: {
    backgroundColor: "#f44336",
    color: "white",
  },
  connectionInfo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  dateInfo: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
  },
  noMedia: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: 100,
    backgroundColor: "#f5f5f5",
    borderRadius: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
}));

const WhatsAppStatusList = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [count, setCount] = useState(0);
  const [searchParam, setSearchParam] = useState("");

  useEffect(() => {
    loadStatuses();
  }, [searchParam, pageNumber]);

  const loadStatuses = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/whatsapp-status", {
        params: {
          searchParam,
          pageNumber,
        },
      });
      setStatuses(data.statuses);
      setHasMore(data.hasMore);
      setCount(data.count);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchParam(e.target.value);
    setPageNumber(1);
  };

  const formatDate = (dateString) => {
    return format(parseISO(dateString), "dd/MM/yyyy HH:mm", { locale: es });
  };

  const getStatusChip = (status) => {
    const isExpired = new Date(status.expiresAt) < new Date();
    return (
      <Chip
        label={isExpired ? "Expirado" : "Activo"}
        className={`${classes.statusChip} ${
          isExpired ? classes.expiredStatus : classes.activeStatus
        }`}
        size="small"
      />
    );
  };

  const renderMedia = (status) => {
    if (!status.mediaUrl) {
      return (
        <Box className={classes.noMedia}>
          <Typography variant="body2">Sin multimedia</Typography>
        </Box>
      );
    }

    const isVideo = status.mediaType?.startsWith('video/');
    
    return (
      <Box className={classes.mediaContainer}>
        {isVideo ? (
          <video
            className={classes.media}
            controls
            style={{ width: '100%' }}
          >
            <source src={status.mediaUrl} type={status.mediaType} />
            Tu navegador no soporta el elemento de video.
          </video>
        ) : (
          <img
            src={status.mediaUrl}
            alt="Estado multimedia"
            className={classes.media}
            style={{ width: '100%' }}
          />
        )}
      </Box>
    );
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Estados Publicados</Title>
        <MainHeaderButtonsWrapper>
          <Tooltip title="Actualizar">
            <IconButton onClick={loadStatuses}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      
      <Paper className={classes.searchContainer} variant="outlined">
        <TextField
          fullWidth
          placeholder="Buscar estados..."
          value={searchParam}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          variant="outlined"
          size="small"
        />
      </Paper>
      
      <Paper className={classes.mainPaper} variant="outlined">
        {loading && (
          <Box p={2}>
            <Typography>Cargando estados...</Typography>
          </Box>
        )}
        
        {!loading && statuses.length === 0 && (
          <Box p={4} textAlign="center">
            <Typography variant="h6" color="textSecondary">
              No se encontraron estados publicados
            </Typography>
          </Box>
        )}

        <Grid container spacing={2}>
          {statuses.map((status) => (
            <Grid item xs={12} sm={6} md={4} key={status.id}>
              <Card className={classes.statusCard}>
                <CardContent>
                  <Box className={classes.connectionInfo}>
                    <Typography variant="subtitle2" color="primary">
                      {status.whatsapp?.name || "Conexión desconocida"}
                    </Typography>
                    {getStatusChip(status)}
                  </Box>
                  
                  <Typography 
                    variant="body1" 
                    className={classes.statusText}
                  >
                    {status.text}
                  </Typography>
                  
                  {renderMedia(status)}
                  
                  <Box mt={2}>
                    <Typography variant="caption" className={classes.dateInfo}>
                      Publicado: {formatDate(status.createdAt)}
                    </Typography>
                    <br />
                    <Typography variant="caption" className={classes.dateInfo}>
                      Expira: {formatDate(status.expiresAt)}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        
        {count > 0 && (
          <Box p={2} textAlign="center">
            <Typography variant="body2" color="textSecondary">
              Mostrando {statuses.length} de {count} estados
            </Typography>
          </Box>
        )}
      </Paper>
    </MainContainer>
  );
};

export default WhatsAppStatusList;